import { Application } from '@pickleballinc/lib/types';

import { FreestarInfo, FreestarPlacementId } from './types';

/**
 * Escape hatch for running the real Freestar stack outside production.
 *
 * MUST be `false` on anything that ships. Freestar is deliberately dead on DEV, TEST and localhost
 * (PW-7587): no preconnects, no bootstrap, no pubfig, no AdShield, and `AdPlaceholder` renders a
 * sized grey box in place of every slot so non-prod layout still matches production.
 *
 * Flip to `true` only for a short, deliberate test — the AD_FREE_PAGES gating and the
 * FreestarRouteGuard product teardown cannot otherwise be exercised before release. It turns on
 * four things at once:
 *   - preconnects + pubfig in HeaderScripts, and the AdShield anti-adblock payload
 *   - real slots instead of AdPlaceholder
 *   - the `page_url` spoof, without which Freestar will not fill on a non-production origin
 *   - the localhost block in RenderFloatingVideoAd
 *
 * While it is on, every DEV, TEST, preview and developer machine pulls real creatives and — because
 * of the spoof — reports them to Freestar as production traffic. Turn it off as soon as you are done.
 */
const FREESTAR_ENABLED_OUTSIDE_PROD = false;

const APPS_HASH = {
	pickleball: 'pickleball-com',
	tournament: 'pickleballtournaments-com',
	clubs: 'pickleballclubs-com',
	leagues: 'pickleballleagues-com',
	'team-leagues': 'pickleballteamleagues-com',
	'apr-rankings': 'amateurpickleballrankings-com'
};

const APPS_DOMAIN_HASH: Record<Application, string> = {
	pickleball: 'pickleball.com',
	tournament: 'pickleballtournaments.com',
	clubs: 'pickleballclubs.com',
	leagues: 'pickleballleagues.com',
	'team-leagues': 'pickleballteamleagues.com',
	'apr-rankings': 'amateurpickleballrankings.com'
};

const PLACEMENTS_HASH: Record<FreestarPlacementId, FreestarInfo> = {
	'pickleball-com_ATF_leaderboard': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __468x60 __970x90`,
		publisher: 'pickleball-com',
		placeholder: { width: 970, height: 90 }
	},
	'pickleball-com_incontent_728_reusable': {
		sizes: `__300x50 __300x100 __320x50 __728x90 __468x60 __320x100`,
		publisher: 'pickleball-com',
		placeholder: { width: 728, height: 90 }
	},
	'pickleball-com_incontent_970_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __468x60 __970x90 __970x250`,
		publisher: 'pickleball-com',
		placeholder: { width: 970, height: 250 }
	},
	'pickleball-com_siderail_right_atf': {
		sizes: `__300x50 __300x100 __300x250`,
		publisher: 'pickleball-com',
		placeholder: { width: 300, height: 240 }
	},
	'pickleball-com_siderail_left_atf': {
		sizes: `__300x50 __300x100 __300x250`,
		publisher: 'pickleball-com',
		placeholder: { width: 300, height: 240 }
	},
	'pickleball-com_siderail_right_sticky': {
		sizes: `__300x600 __160x600 __120x600`,
		publisher: 'pickleball-com',
		placeholder: { width: 300, height: 580 }
	},
	'pickleball-com_siderail_left_sticky': {
		sizes: `__300x600 __160x600 __120x600`,
		publisher: 'pickleball-com',
		placeholder: { width: 300, height: 580 }
	},
	'pickleball-com_siderail_right_atf_article': {
		sizes: `__300x600 __160x600 __120x600`,
		publisher: 'pickleball-com',
		placeholder: { width: 300, height: 580 }
	},
	'pickleball-com_siderail_left_atf_article': {
		sizes: `__300x600 __160x600 __120x600`,
		publisher: 'pickleball-com',
		placeholder: { width: 300, height: 580 }
	},
	'pickleball-com_siderail_right_sticky_article': {
		sizes: `__300x600 __160x600 __120x600`,
		publisher: 'pickleball-com',
		placeholder: { width: 300, height: 580 }
	},
	'pickleball-com_siderail_left_sticky_article': {
		sizes: `__300x600 __160x600 __120x600`,
		publisher: 'pickleball-com',
		placeholder: { width: 300, height: 580 }
	},
	'pickleball-com_incontent_300x250_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __300x250 __336x280`,
		publisher: 'pickleball-com',
		placeholder: { width: 300, height: 250 }
	},
	'pickleballtournaments-com_leaderboard_atf': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __468x60 __970x90`,
		publisher: 'pickleballtournaments-com',
		placeholder: { width: 970, height: 90 }
	},
	'pickleballtournaments-com_incontent_728_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __468x60 __728x90`,
		publisher: 'pickleballtournaments-com',
		placeholder: { width: 728, height: 90 }
	},
	'pickleballtournaments-com_incontent_970_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __970x90 __468x60 __970x250`,
		publisher: 'pickleballtournaments-com',
		placeholder: { width: 970, height: 250 }
	},
	'pickleballtournaments-com_siderail_right_blog': {
		sizes: `__300x600 __160x600 __120x600 __160x600`,
		publisher: 'pickleballtournaments-com',
		placeholder: { width: 300, height: 600 }
	},
	'pickleballtournaments-com_siderail_left_blog': {
		sizes: `__300x600 __160x600 __120x600 __160x600`,
		publisher: 'pickleballtournaments-com',
		placeholder: { width: 300, height: 600 }
	},
	'pickleballtournaments-com_siderail_right_tournaments': {
		sizes: `__300x600 __160x600 __120x600 __160x600`,
		publisher: 'pickleballtournaments-com',
		placeholder: { width: 300, height: 600 }
	},
	'pickleballtournaments-com_incontent_300x250_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __300x250 __336x280`,
		publisher: 'pickleballtournaments-com',
		placeholder: { width: 300, height: 250 }
	},
	'pickleballclubs-com_leaderboard_atf': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __468x60 __970x90`,
		publisher: 'pickleballclubs-com',
		placeholder: { width: 970, height: 90 }
	},
	'pickleballclubs-com_incontent_728_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __468x60 __728x90`,
		publisher: 'pickleballclubs-com',
		placeholder: { width: 728, height: 90 }
	},
	'pickleballclubs-com_incontent_970_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __970x90 __468x60 __970x250`,
		publisher: 'pickleballclubs-com',
		placeholder: { width: 970, height: 250 }
	},
	'pickleballleagues-com_incontent_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __300x250 __336x280`,
		publisher: 'pickleballleagues-com',
		placeholder: { width: 300, height: 250 }
	},
	'pickleballleagues-com_leaderboard_atf': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __468x60 __970x90`,
		publisher: 'pickleballleagues-com',
		placeholder: { width: 970, height: 90 }
	},
	'pickleballleagues-com_incontent_728_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __468x60 __728x90`,
		publisher: 'pickleballleagues-com',
		placeholder: { width: 728, height: 90 }
	},
	'pickleballleagues-com_incontent_970_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __970x90 __468x60 __970x250`,
		publisher: 'pickleballleagues-com',
		placeholder: { width: 970, height: 250 }
	},
	'pickleballleagues-com_incontent_300x250_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __300x250 __336x280`,
		publisher: 'pickleballleagues-com',
		placeholder: { width: 300, height: 250 }
	},
	'pickleballteamleagues-com_incontent_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __300x250 __336x280`,
		publisher: 'pickleballteamleagues-com',
		placeholder: { width: 300, height: 250 }
	},
	'pickleballteamleagues-com_leaderboard_atf': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __468x60 __970x90`,
		publisher: 'pickleballteamleagues-com',
		placeholder: { width: 970, height: 90 }
	},
	'pickleballteamleagues-com_incontent_728_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __468x60 __728x90`,
		publisher: 'pickleballteamleagues-com',
		placeholder: { width: 728, height: 90 }
	},
	'pickleballteamleagues-com_incontent_970_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __970x90 __468x60 __970x250`,
		publisher: 'pickleballteamleagues-com',
		placeholder: { width: 970, height: 250 }
	},
	'pickleballteamleagues-com_siderail_right_team_leagues': {
		sizes: `__300x600 __160x600 __120x600 __160x600`,
		publisher: 'pickleballteamleagues-com',
		placeholder: { width: 300, height: 600 }
	},
	'worldpickleballrankings-com_leaderboard_atf': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __468x60 __970x90`,
		publisher: 'worldpickleballrankings-com',
		placeholder: { width: 970, height: 90 }
	},
	'worldpickleballrankings-com_incontent_728_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __468x60 __728x90`,
		publisher: 'worldpickleballrankings-com',
		placeholder: { width: 728, height: 90 }
	},
	'worldpickleballrankings-com_incontent_970_reusable': {
		sizes: `__300x50 __300x100 __320x50 __320x100 __728x90 __970x90 __468x60 __970x250`,
		publisher: 'worldpickleballrankings-com',
		placeholder: { width: 970, height: 250 }
	}
};

export { APPS_HASH, APPS_DOMAIN_HASH, PLACEMENTS_HASH, FREESTAR_ENABLED_OUTSIDE_PROD };
